import React from "react";
import dayjs from "dayjs";
import { Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";

import WorkerWorkedDayResponse from "../../Repositories/WorkerResponses/WorkerWorkedDayResponse";
import WorkerWorkedPeriodResponse from "../../Repositories/WorkerResponses/WorkerWorkedPeriodResponse";

export interface WorkerCountPeriodDetails {
    countPeriodId: string;
    countPeriodStartDate: string;
    countPeriodEndDate: string;
    targetStartDate: string;
    targetEndDate: string;
    cashTip: number;
    pinTip: number;
}

interface WorkerCountPeriodDetailsDialogProps {
    open: boolean;
    monthKey: string;
    cashTip: number;
    pinTip: number;
    countPeriods: WorkerCountPeriodDetails[];
    workDays: WorkerWorkedDayResponse[];
    onClose: () => void;
}

interface DisplayWorkDay {
    id: string;
    date: string;
    workPeriods: WorkerWorkedPeriodResponse[];
}

const ACCENT = "#c79656";
const ACCENT_LIGHT = "rgba(199,150,86,0.10)";
const ACCENT_BORDER = "rgba(199,150,86,0.22)";
const ACCENT_TEXT = "#8a5d21";
const SURFACE = "#ffffff";
const BORDER = "rgba(0,0,0,0.07)";
const TEXT_PRIMARY = "#18181b";
const TEXT_SECONDARY = "rgba(24,24,27,0.52)";
const BG_ROW = "rgba(0,0,0,0.025)";

function formatCurrency(amount: number) {
    return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
    }).format(amount || 0);
}

function pluralize(count: number, singular: string, plural: string) {
    return `${count} ${count === 1 ? singular : plural}`;
}

function formatDateRange(startDate: string, endDate: string) {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    if (start.isSame(end, "day")) return start.format("D MMM YYYY");
    return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
}

function isInsideRange(date: string, startDate: string, endDate: string) {
    const value = dayjs(date).startOf("day");
    const start = dayjs(startDate).startOf("day");
    const end = dayjs(endDate).endOf("day");
    return (
        (value.isAfter(start) || value.isSame(start, "day")) &&
        (value.isBefore(end) || value.isSame(end, "day"))
    );
}

function getRelevantWorkDays(
    workDays: WorkerWorkedDayResponse[],
    countPeriods: WorkerCountPeriodDetails[],
    monthKey: string
): DisplayWorkDay[] {
    const monthStart = dayjs(`${monthKey}-01`).startOf("month");
    const ranges =
        countPeriods.length > 0
            ? countPeriods.map((period) => ({
                  startDate: period.targetStartDate,
                  endDate: period.targetEndDate,
              }))
            : [
                  {
                      startDate: monthStart.format("YYYY-MM-DD"),
                      endDate: monthStart.endOf("month").format("YYYY-MM-DD"),
                  },
              ];

    return workDays
        .map((workDay) => {
            const workPeriods = (workDay.workPeriods ?? [])
                .filter((period) =>
                    ranges.some((range) =>
                        isInsideRange(
                            period.date || workDay.date,
                            range.startDate,
                            range.endDate
                        )
                    )
                )
                .sort(
                    (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf()
                );

            return { id: workDay.id, date: workDay.date, workPeriods };
        })
        .filter((workDay) => workDay.workPeriods.length > 0)
        .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());
}

// ─── Stat card ───────────────────────────────────────────────────────────────

interface StatCardProps {
    icon: React.ReactNode;
    label: string;
    value: string;
    accent?: boolean;
}

function StatCard({ icon, label, value, accent }: StatCardProps) {
    return (
        <div
            style={{
                borderRadius: 12,
                border: `1px solid ${accent ? ACCENT_BORDER : BORDER}`,
                background: accent ? ACCENT_LIGHT : SURFACE,
                padding: "18px 20px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
            }}
        >
            <div style={{ color: accent ? ACCENT : TEXT_SECONDARY }}>{icon}</div>
            <span
                style={{
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 0.9,
                    color: accent ? ACCENT_TEXT : TEXT_SECONDARY,
                }}
            >
                {label}
            </span>
            <span
                style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: accent ? ACCENT_TEXT : TEXT_PRIMARY,
                    lineHeight: 1,
                }}
            >
                {value}
            </span>
        </div>
    );
}

// ─── Pill badge ──────────────────────────────────────────────────────────────

function Pill({
    children,
    variant = "neutral",
}: {
    children: React.ReactNode;
    variant?: "accent" | "neutral";
}) {
    return (
        <span
            style={{
                borderRadius: 6,
                background:
                    variant === "accent" ? ACCENT_LIGHT : "rgba(0,0,0,0.05)",
                color: variant === "accent" ? ACCENT_TEXT : "rgba(24,24,27,0.68)",
                padding: "3px 8px",
                fontSize: 11,
                fontWeight: 700,
                whiteSpace: "nowrap",
            }}
        >
            {children}
        </span>
    );
}

// ─── Section header ──────────────────────────────────────────────────────────

function SectionLabel({
    children,
    right,
}: {
    children: React.ReactNode;
    right?: React.ReactNode;
}) {
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
                gap: 12,
            }}
        >
            <span
                style={{
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: "uppercase",
                    letterSpacing: 0.9,
                    color: TEXT_SECONDARY,
                }}
            >
                {children}
            </span>
            {right && (
                <span style={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY }}>
                    {right}
                </span>
            )}
        </div>
    );
}

// ─── Worked days table ───────────────────────────────────────────────────────

function WorkedDaysTable({ workDays, style }: { workDays: DisplayWorkDay[]; style?: React.CSSProperties }) {
    if (workDays.length === 0) {
        return (
            <div
                style={{
                    borderRadius: 10,
                    border: "1px dashed rgba(199,150,86,0.30)",
                    background: "rgba(199,150,86,0.04)",
                    padding: "20px 18px",
                    color: TEXT_SECONDARY,
                    fontSize: 13,
                    lineHeight: 1.5,
                    ...style,
                }}
            >
                No worked days were returned for this count period.
            </div>
        );
    }

    return (
        <div
            style={{
                borderRadius: 10,
                border: `1px solid ${BORDER}`,
                overflow: "hidden",
                flex: 1,
                overflowY: "auto",
                minHeight: 0,
                ...style,
            }}
        >
            {/* Table header */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(120px, 1.4fr) minmax(0, 2fr) 90px 90px",
                    gap: 0,
                    padding: "8px 14px",
                    background: "rgba(0,0,0,0.03)",
                    borderBottom: `1px solid ${BORDER}`,
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                }}
            >
                {["Date", "Period", "Cash", "Pin"].map((col, i) => (
                    <span
                        key={col}
                        style={{
                            fontSize: 10,
                            fontWeight: 800,
                            textTransform: "uppercase",
                            letterSpacing: 0.8,
                            color: TEXT_SECONDARY,
                            textAlign: i >= 2 ? "right" : "left",
                        }}
                    >
                        {col}
                    </span>
                ))}
            </div>

            {/* Rows grouped by day */}
            {workDays.map((workDay, dayIdx) => {
                const isEvenGroup = dayIdx % 2 === 0;
                const groupBg = isEvenGroup ? SURFACE : "rgba(0,0,0,0.016)";

                return workDay.workPeriods.map((period, periodIdx) => {
                    const isFirstInDay = periodIdx === 0;
                    const isLastInDay = periodIdx === workDay.workPeriods.length - 1;
                    const isLastDay = dayIdx === workDays.length - 1;
                    const showGroupDivider = isLastInDay && !isLastDay;

                    return (
                        <div
                            key={period.id}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(120px, 1.4fr) minmax(0, 2fr) 90px 90px",
                                gap: 0,
                                padding: "9px 14px",
                                background: groupBg,
                                borderBottom: showGroupDivider
                                    ? "1px solid rgba(0,0,0,0.10)"
                                    : `1px solid ${BORDER}`,
                                alignItems: "center",
                            }}
                        >
                            {/* Date column — show date on first row, connector line on subsequent */}
                            <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
                                {isFirstInDay ? (
                                    <span style={{ fontSize: 12, fontWeight: 700, color: TEXT_PRIMARY }}>
                                        {dayjs(workDay.date).format("ddd D MMM")}
                                    </span>
                                ) : (
                                    <span style={{
                                        display: "inline-block",
                                        width: 10,
                                        height: "100%",
                                        minHeight: 20,
                                        borderLeft: "2px solid rgba(0,0,0,0.10)",
                                        marginLeft: 6,
                                        borderBottom: isLastInDay ? undefined : undefined,
                                    }} />
                                )}
                            </div>

                            {/* Period name */}
                            <div style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: TEXT_PRIMARY,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                paddingRight: 8,
                            }}>
                                {period.name?.trim() || "Work period"}
                            </div>

                            {/* Cash */}
                            <div style={{ textAlign: "right" }}>
                                <span style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: ACCENT_TEXT,
                                    background: ACCENT_LIGHT,
                                    borderRadius: 5,
                                    padding: "2px 7px",
                                }}>
                                    {formatCurrency(period.cashTip)}
                                </span>
                            </div>

                            {/* Pin */}
                            <div style={{ textAlign: "right" }}>
                                <span style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: "rgba(24,24,27,0.68)",
                                    background: "rgba(0,0,0,0.05)",
                                    borderRadius: 5,
                                    padding: "2px 7px",
                                }}>
                                    {formatCurrency(period.pinTip)}
                                </span>
                            </div>
                        </div>
                    );
                });
            })}
        </div>
    );
}

// ─── Main dialog ─────────────────────────────────────────────────────────────

export default function WorkerCountPeriodDetailsDialog({
    open,
    monthKey,
    cashTip,
    pinTip,
    countPeriods,
    workDays,
    onClose,
}: WorkerCountPeriodDetailsDialogProps) {
    const monthStart = dayjs(`${monthKey}-01`).startOf("month");
    const relevantWorkDays = getRelevantWorkDays(workDays, countPeriods, monthKey);
    const totalTip = cashTip + pinTip;
    const totalWorkPeriods = relevantWorkDays.reduce(
        (sum, workDay) => sum + workDay.workPeriods.length,
        0
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    border: `1px solid ${BORDER}`,
                    boxShadow: "0 24px 80px rgba(0,0,0,0.20)",
                    overflow: "hidden",
                    minHeight: "88vh",
                    display: "flex",
                    flexDirection: "column",
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: "rgba(0,0,0,0.38)",
                        backdropFilter: "blur(5px)",
                    },
                },
            }}
        >
            {/* ── Header ── */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    py: 2.5,
                    px: 3,
                    borderBottom: `1px solid ${BORDER}`,
                    background:
                        "linear-gradient(180deg, rgba(199,150,86,0.08), rgba(255,255,255,0))",
                    color: TEXT_PRIMARY,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                        minWidth: 0,
                    }}
                >
                    <div
                        style={{
                            width: 42,
                            height: 42,
                            borderRadius: 11,
                            background: ACCENT_LIGHT,
                            border: `1px solid ${ACCENT_BORDER}`,
                            display: "grid",
                            placeItems: "center",
                            color: ACCENT,
                            flex: "0 0 auto",
                        }}
                    >
                        <CalendarMonthRoundedIcon fontSize="small" />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 20, fontWeight: 800, lineHeight: 1.2 }}>
                            {countPeriods.length > 0
                                ? formatDateRange(countPeriods[0].countPeriodStartDate, countPeriods[0].countPeriodEndDate)
                                : monthStart.format("MMMM YYYY")}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY }}>
                                {monthStart.format("MMMM YYYY")}
                            </span>
                            {countPeriods.length > 0 && (
                                <>
                                    <span style={{ fontSize: 10, color: TEXT_SECONDARY, opacity: 0.5 }}>·</span>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: ACCENT_TEXT, background: ACCENT_LIGHT, borderRadius: 5, padding: "1px 7px" }}>
                                        Cash {formatCurrency(countPeriods[0].cashTip)}
                                    </span>
                                    <span style={{ fontSize: 12, fontWeight: 600, color: TEXT_SECONDARY, background: "rgba(0,0,0,0.05)", borderRadius: 5, padding: "1px 7px" }}>
                                        Pin {formatCurrency(countPeriods[0].pinTip)}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <IconButton size="small" onClick={onClose} aria-label="Close">
                    <CloseRoundedIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            {/* ── Body ── */}
            <DialogContent sx={{ p: 3, pt: "24px !important", background: "#fafafa", flex: 1, display: "flex", flexDirection: "column" }}>

                {/* Stat cards */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                        gap: 12,
                        marginBottom: 20,
                    }}
                >
                    <StatCard
                        icon={<PaidRoundedIcon style={{ fontSize: 18 }} />}
                        label="Earned tip"
                        value={formatCurrency(totalTip)}
                        accent
                    />
                    <StatCard
                        icon={<PaidRoundedIcon style={{ fontSize: 18 }} />}
                        label="Cash tip"
                        value={formatCurrency(cashTip)}
                    />
                    <StatCard
                        icon={<PaidRoundedIcon style={{ fontSize: 18 }} />}
                        label="Pin tip"
                        value={formatCurrency(pinTip)}
                    />
                    <StatCard
                        icon={<CalendarMonthRoundedIcon style={{ fontSize: 18 }} />}
                        label="Worked days"
                        value={String(relevantWorkDays.length)}
                    />
                    <StatCard
                        icon={<ScheduleRoundedIcon style={{ fontSize: 18 }} />}
                        label="Work periods"
                        value={String(totalWorkPeriods)}
                    />
                </div>

                {/* Worked days */}
                <div
                    style={{
                        borderRadius: 12,
                        border: `1px solid ${BORDER}`,
                        background: SURFACE,
                        padding: "18px 20px",
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                    }}
                >
                    <SectionLabel
                        right={`${pluralize(relevantWorkDays.length, "day", "days")} · ${pluralize(totalWorkPeriods, "period", "periods")}`}
                    >
                        Worked days
                    </SectionLabel>

                    <WorkedDaysTable workDays={relevantWorkDays} style={{ flex: 1, minHeight: 0 }} />
                </div>
            </DialogContent>
        </Dialog>
    );
}